import { Mission, Sample, SampleBox, SamplingSpec, Session } from './db';

import EventBus from './EventBus';
import { SCAN_EVENTS } from './scanEvents';
import { LocalStorageGenerator, wait } from './utils';
import { createSampleMap } from './dataModelHelpers';

import {
  alertError,
  alertWarn,
  alertConfirm,
  alertSuccess,
  alertFullScreen,
  cancelConfirmTag,
} from './alertDispatcher';
import { isBarcodeValidBag, isBarcodeValidBox } from './barcode_rules';

import logger from './logger';
import { getCurrentMission, getCurrentSession } from './dataModelHelpers';

import { successTone, errorTone, warningTone } from './alertTones';
import { SampleErrorMap, SampleErrors } from './types/types';
import { dispatchUpdatedCurrentSample } from './sessionEvents';
import { SampleList } from './db/SampleClass';

import * as Sentry from '@sentry/react';
import { dispatchRosMsgPub } from './RobotConnection';
import { OUTBOUND_TOPICS } from './types/rosTopics';
import { CoreCountEnforcementEnabled, ScannerPauseDelayMs } from './db/local_storage';

export interface BarcodeProcessorState {
  barcodeScanningPaused: boolean;
  boxScanningPaused: boolean;
}

export const BarcodeProcessorStateStore = LocalStorageGenerator<Partial<BarcodeProcessorState>>(
  'BarcodeProcessorStateStore',
  {
    barcodeScanningPaused: false,
    boxScanningPaused: false,
  },
);

let pause = false;

/** Why a scanned barcode could not be accepted for the current sample. */
export type ScanFailureCode =
  | 'NO_SESSION'
  | 'NO_MISSION'
  | 'NO_CURRENT_SAMPLE'
  | 'INVALID_BARCODE'
  | 'DUPLICATE_BARCODE'
  | 'NO_SPEC'
  | 'MULTIPLE_OPEN_BOXES'
  | 'WRONG_LAB_BOX'
  | 'BOX_DECLINED'
  | 'BOX_ADD_FAILED'
  | 'BOX_CLOSED';

/**
 * Already reported elsewhere: logged for support, never alerted. `addAndSelectBox`
 * alerts its own cause, and declining the prompt is a deliberate "no".
 */
const NO_ALERT_SCAN_FAILURES: ReadonlySet<ScanFailureCode> = new Set<ScanFailureCode>([
  'BOX_DECLINED',
  'BOX_ADD_FAILED',
]);

/** No tone either: a deliberate "no" is not a failure. */
const NO_TONE_SCAN_FAILURES: ReadonlySet<ScanFailureCode> = new Set<ScanFailureCode>(['BOX_DECLINED']);

/**
 * Failures worth a Sentry event: they point at config/data problems a human
 * needs to look at. Operator-fixable failures (invalid/duplicate barcode,
 * nothing selected, declined, multiple open boxes, wrong-lab box, closed box)
 * are self-recoverable and high volume, so they stay in the event log only.
 */
const SENTRY_REPORTED_SCAN_FAILURES: ReadonlySet<ScanFailureCode> = new Set<ScanFailureCode>([
  'NO_SESSION',
  'NO_MISSION',
  'NO_SPEC',
  'BOX_ADD_FAILED',
]);

export interface ScanFailure {
  ok: false;
  code: ScanFailureCode;
  /**
   * Operator-facing message. Invalid barcodes deliberately include the raw
   * barcode AND the raw regex the deal restricts bags to — that is what made
   * CU-86b9j43xt invisible in the field.
   */
  message: string;
  /** The raw string the scanner delivered, no cleaning. */
  barcode: string;
  sampleId?: string;
  /** Extra structured fields for the event log / Sentry. */
  context?: Record<string, unknown>;
}

/**
 * A successful scan check carries the box the scan may be written into, so the
 * caller never has to re-read "whatever box happens to be active".
 */
export type SaveVerdict = { ok: true; box: SampleBox } | ScanFailure;

function scanFailure(
  code: ScanFailureCode,
  message: string,
  barcode: string,
  extra: { sampleId?: string; context?: Record<string, unknown> } = {},
): ScanFailure {
  return { ok: false, code, message, barcode, ...extra };
}

/**
 * The single generic reporter for scan failures: alert + tone + durable log.
 * Case-specific UI (the duplicate "where is the bag" screen, the box prompt)
 * may add its own message on top — a couple of messages beats silence.
 * Deliberate operator choices (BOX_DECLINED) are only logged; every other
 * failure re-beeps on every attempt.
 *
 * Sentry events are reserved for failures that point at config/data problems.
 * Operator mistakes (wrong bag, duplicate, nothing selected) are frequent and
 * self-recoverable — they go to the event log, not to Sentry, so a shift of bad
 * scans can't flood the project.
 * @param {ScanFailure} failure The verdict returned by validateCanSave
 */
export function reportScanFailure(failure: ScanFailure) {
  if (!NO_TONE_SCAN_FAILURES.has(failure.code)) {
    errorTone();
  }

  if (!NO_ALERT_SCAN_FAILURES.has(failure.code)) {
    alertError(failure.message);
  }

  const payload = {
    code: failure.code,
    barcode: failure.barcode,
    message: failure.message,
    sampleId: failure.sampleId,
    ...failure.context,
  };

  logger.log('SCAN_FAILED', payload);

  if (SENTRY_REPORTED_SCAN_FAILURES.has(failure.code)) {
    // captureMessage, not setContext: setContext alone never emits an event and
    // leaves the context on the scope, decorating the next unrelated exception
    Sentry.captureMessage(`Scan failed: ${failure.code}`, {
      level: 'warning',
      fingerprint: ['scan-failure', failure.code],
      contexts: { scanFailure: payload },
    });
  }
}

/** Best-effort human readable message for an unknown thrown value. */
function describeError(err: unknown): string {
  if (err instanceof Error) {
    return err.message;
  }

  if (typeof err === 'string') {
    return err;
  }

  try {
    return JSON.stringify(err) ?? String(err);
  } catch {
    return String(err);
  }
}

/**
 * Validates a barcode and saves if it is valid
 * @param {string} detectedCode The code detected
 */
export async function processScan(detectedCode: string) {
  if (pause) {
    // not silent in the logs: a stuck pause flag looks exactly like a dead scanner
    logger.log('SCAN_IGNORED_WHILE_BUSY', { detectedCode });

    return;
  }

  pause = true;

  try {
    // inside the try: a throw here used to reject the whole scan with no feedback
    await SampleBox.logBoxes('BOXES_BEFORE_SCAN', true);

    console.log('BARCODE', { detectedCode });

    if (isBarcodeValidBox(detectedCode)) {
      if (BarcodeProcessorStateStore.get().boxScanningPaused) {
        errorTone();
        alertWarn(`Scanning paused`);

        return;
      }

      EventBus.dispatch(SCAN_EVENTS.SCANNED_BOX, detectedCode);
    } else {
      if (BarcodeProcessorStateStore.get().barcodeScanningPaused) {
        errorTone();
        alertWarn('Barcode scanning is currently paused');

        return;
      }

      const session = getCurrentSession();

      if (!session) {
        reportScanFailure(scanFailure('NO_SESSION', `No current session! Please soft reset your robot`, detectedCode));

        return;
      }

      const mission = getCurrentMission();

      if (!mission) {
        reportScanFailure(scanFailure('NO_MISSION', `No current mission found!`, detectedCode));

        return;
      }

      const currentSample = session.getSample();

      if (!currentSample) {
        reportScanFailure(scanFailure('NO_CURRENT_SAMPLE', `No current sample selected for scanning!`, detectedCode));

        return;
      }

      const verdict = await validateCanSave(detectedCode, currentSample);

      if (!verdict.ok) {
        reportScanFailure(verdict);

        return;
      }

      const { pulledCoresReady, requiredPulledCoresNumber, pulledCoresNumber } = currentSample.hasPulledCoresReady();

      const strictCoreEnforcement = CoreCountEnforcementEnabled.get();

      if (strictCoreEnforcement && !pulledCoresReady) {
        errorTone();
        alertError(
          `Please record the right number of cores before scanning a barcode: (${requiredPulledCoresNumber} required, ${pulledCoresNumber} pulled)!`,
        );

        return;
      }

      const job = mission.getJob();
      const measurementsRequired = job?.manual_measurement_required;
      const coresMissingMeasurements = currentSample.getSoilCores().filter((core) => !core.core_length_cm);
      const sampleHasAllMeasurements = coresMissingMeasurements.length === 0;

      if (strictCoreEnforcement && measurementsRequired && !sampleHasAllMeasurements) {
        errorTone();
        alertError(
          `Please record all core measurements before scanning a barcode! Missing measurement for core ${coresMissingMeasurements.map((core) => core.name).join(', ')}`,
        );

        return;
      }

      // the verdict carries the resolved box, so the scan cannot land in a
      // wrong-lab box that merely happens to be active
      const box = verdict.box;
      const boxSamples = box.getSamples();
      const hasBarcode = Boolean(currentSample.bag_id);
      let overwrite = false;

      if (hasBarcode) {
        errorTone();
        const okToWrite = await alertConfirm(
          `Sample ${currentSample.sample_id} already has barcode! Do you want to overwrite?`,
        );

        if (!okToWrite) {
          return;
        } else {
          // TODO display large popup with barcode/bag to remove from box
          const sampleIndex = boxSamples.findIndex((sample) => sample.instance_id === currentSample.instance_id);
          const removed = await alertFullScreen(
            `Please remove bag ${currentSample.bag_id}`,
            `Box Order Packing Position: ${sampleIndex + 1}`,
            ['Ok'],
          );
          logger.log('BARCODE', `Removed sample? ${removed}`);
          overwrite = true;
        }
      }

      console.log(`Overwrite? ${overwrite}`);

      // the box can be closed while the overwrite/packing dialogs are open; that is
      // an operator action, not a crash, so report it like any other scan failure.
      // Rehydrate the box: this object was read before the dialogs and its private
      // state is only refreshed by a table lookup.
      const freshBox = SampleBox.get(box.instance_id);

      if (!freshBox || freshBox.closed) {
        reportScanFailure(
          scanFailure(
            'BOX_CLOSED',
            `Box ${box.short_uid} was closed during the scan. Please scan again.`,
            detectedCode,
            {
              sampleId: currentSample.sample_id,
              context: { boxUid: box.short_uid, boxInstanceId: box.instance_id },
            },
          ),
        );

        return;
      }

      await checkBoxPackingLimits(boxSamples);

      const errMap = await setSampleBarcodes(currentSample.instance_id, detectedCode, false, box.instance_id);

      successTone();
      alertSuccess(
        `Successfully scanned barcode ${detectedCode} for sample ${currentSample.sample_id} into ${box.short_uid}!`,
        {
          actions: {
            Reject: async () => {
              const errMap = await setSampleBarcodes(currentSample.instance_id, '', false, box.instance_id);
              await checkGapError(errMap);
              EventBus.dispatch(SCAN_EVENTS.SCANNED_BARCODE, {
                barcode: '',
                currentSampleInstanceId: currentSample.instance_id,
                errMap,
                nextSampleInstanceId: currentSample.instance_id,
              });
              const oldSampleInstanceId = session.Sample_id;
              session.Sample_id = currentSample.instance_id;
              dispatchUpdatedCurrentSample([oldSampleInstanceId, currentSample.instance_id]);
            },
          },
        },
      );

      await checkGapError(errMap); // alerts user if scanned sample had any errors

      const nextSampleInstanceId: number | undefined = undefined;

      console.log(`barcodes.ts processScan nextSampleInstanceId`, nextSampleInstanceId);

      EventBus.dispatch(SCAN_EVENTS.SCANNED_BARCODE, {
        barcode: detectedCode,
        currentSampleInstanceId: currentSample.instance_id,
        errMap,
        nextSampleInstanceId,
      });

      const oldSampleInstanceId = session.Sample_id;
      session.Sample_id = nextSampleInstanceId;
      dispatchUpdatedCurrentSample([oldSampleInstanceId, nextSampleInstanceId]);
    }
  } catch (err) {
    // capture before alerting: the alert path must never swallow the report
    Sentry.captureException(err, { contexts: { processScan: { detectedCode } } });
    console.warn(err);

    logger.log('SCAN_FAILED_UNEXPECTED', { detectedCode, error: describeError(err) });

    errorTone();
    alertError(
      `Scanning barcode ${detectedCode} failed unexpectedly: ${describeError(err)}. Please retry; if it keeps failing, call ops.`,
    );
  } finally {
    await wait(ScannerPauseDelayMs.get()); // wait 1 second to prevent double scans
    pause = false;

    await SampleBox.logBoxes('BOXES_AFTER_SCAN', true);
  }
}

async function checkBoxPackingLimits(boxSamples: SampleList) {
  const filled = boxSamples.filter((sample) => sample.bag_id).length;
  console.log(`barcodes:processScan:box has ${filled + 1} filled samples`);

  switch (filled + 1) {
    case 30:
      alertWarn(`You now have ${filled + 1} samples in your box. Please be sure to close your box if it is full`);
      break;
    case 35:
    case 40:
    case 45:
      await alertFullScreen(
        'Did you forget to close your digital box?',
        `You have now boxed <strong>${filled + 1}</strong> samples in your box`,
        ['Ok'],
      );
  }
}

/**
 * Checks that all barcodes are valid
 */
export function allBarcodesValid() {
  const mission = getCurrentMission();

  if (!mission) {
    return false;
  }

  const samples = mission.getAllSamples();
  const errMap = generateErrorMap(samples);
  const { dupError, gapError, invalidError } = getErrorsFromMap(errMap);
  console.log('ERROR?', dupError, gapError, invalidError);

  return !dupError && !gapError && !invalidError;
}

/**
 * Creates a map of errors for a given array of samples
 * @param {Sample[]} samples
 * @returns {SampleErrorMap} Samples mapped to errors
 */
export function generateErrorMap(samples: Sample[]) {
  const errMap = createSampleMap(samples);

  if (!samples) {
    return errMap;
  }

  const bagIds: string[] = []; // used to keep track of bag ids to check for duplicates

  let lastSampleIdx: number | null = null;

  for (let sampleIdx = 0; sampleIdx < samples.length; sampleIdx++) {
    const sample = samples[sampleIdx];

    if (
      (sample.bag_id && lastSampleIdx !== null && sampleIdx - lastSampleIdx !== 1) ||
      (sample.bag_id && lastSampleIdx === null && sampleIdx !== 0)
    ) {
      for (
        let sampleGapIdx = lastSampleIdx === null ? 0 : lastSampleIdx + 1;
        sampleGapIdx < sampleIdx;
        sampleGapIdx++
      ) {
        const sampleWithGap = samples[sampleGapIdx];

        if (sampleWithGap.sample_id !== undefined && !sampleWithGap.skipped_or_deleted) {
          errMap[sampleWithGap.sample_id].gapError = true;
        }
      }
    }

    if (sample.bag_id) {
      lastSampleIdx = sampleIdx;
    }

    if (
      sample.bag_id &&
      bagIds.find((existId) => existId === sample.bag_id) &&
      !sample.skipped_or_deleted &&
      sample.sample_id !== undefined
    ) {
      errMap[sample.sample_id].dupError = true;
    }

    if (
      sample.bag_id &&
      !isBarcodeValidBag(sample.bag_id, sample.getBarcodeRegex()) &&
      sample.sample_id !== undefined
    ) {
      errMap[sample.sample_id].invalidError = true;
    }

    if (sample.bag_id) {
      bagIds.push(sample.bag_id);
    }
  }

  return errMap;
}

/**
 * Checks error map for duplicate error, gap error, and invalid error
 * @param {object} errMap Error map to check
 * @returns {object} Contains information for what type of errors are present
 */
export function getErrorsFromMap(errMap: SampleErrorMap) {
  let dupError = false;
  let gapError = false;
  let invalidError = false;

  const checkError = (sampleErr: SampleErrors) => {
    if (sampleErr.dupError) {
      dupError = true;
    }

    if (sampleErr.gapError) {
      gapError = true;
    }

    if (sampleErr.invalidError) {
      invalidError = true;
    }
  };

  for (const sampleErr of Object.values(errMap)) {
    checkError(sampleErr);
  }

  return { dupError, gapError, invalidError };
}

/**
 * Checks error map for a gap
 * @param {object} errMap Error map to check
 */
export async function checkGapError(errMap: SampleErrorMap) {
  // local function to do error checking for a single sample
  const _checkGapError = async (sampleErr: SampleErrors) => {
    if (sampleErr.gapError) {
      await errorTone();
      alertWarn(`There is a gap detected in the scanned samples! Please check the mission panel!`);

      return true;
    }
  };

  for (const sampleErr of Object.values(errMap)) {
    if (await _checkGapError(sampleErr)) {
      break;
    }
  }
}

/**
 * Finds the next sample given a sample
 * @param   {Sample} currentSample The current sample id
 * @returns {Sample} The next sample
 */
export function findNextSample(currentSample?: Sample) {
  const mission = getCurrentMission();

  if (!mission) {
    return undefined;
  }

  const allSamples = mission.getAllSamples();
  let sampleIdx = 0;

  // If we have a sample id, find the index of the sample with that id
  if (currentSample) {
    sampleIdx = allSamples.findIndex((sample: Sample) => sample.sample_id === currentSample.sample_id);
    // If we don't have a sample id, return the instance id of the first sample
  } else {
    return allSamples[0];
  }

  // If the sample index is the last index in the array, return the instance id of the last sample
  if (sampleIdx === allSamples.length - 1) {
    return allSamples[allSamples.length - 1];
    // If the sample index is -1, log an error
  } else if (sampleIdx === -1) {
    console.error(`Could not find sample! ${currentSample.sample_id}`);
    // Otherwise, return the instance id of the next sample
  } else {
    return allSamples[sampleIdx + 1];
  }
}

interface BoxLookup {
  box?: SampleBox;
  /** Set when the lookup is ambiguous: more than one open box for the lab. */
  multipleOpenBoxesFor?: string;
}

/**
 * Find an open box matching the required lab code.
 * Does not alert: the caller turns a miss into a ScanFailure so that there is
 * exactly one alert site for scan failures.
 */
function findOrActivateMatchingBox(session: Session, specLabCode: number, specLabName: string): BoxLookup {
  const openBoxes = SampleBox.getOpenBoxes();
  const matchingOpenBoxes = openBoxes.filter((box) => box?.labCode === specLabCode && !box.closed);

  if (matchingOpenBoxes.length > 1) {
    return { multipleOpenBoxesFor: specLabName };
  }

  if (matchingOpenBoxes.length === 1) {
    return { box: session.makeBoxActive(matchingOpenBoxes[0]) };
  }

  return {};
}

/**
 * Checks for any potential failures before saving, only used for scanning.
 * Returns a verdict instead of reporting: `processScan` routes it through
 * `reportScanFailure`. The duplicate check keeps its own richer "where is the
 * bag" UI, so a duplicate can legitimately produce two messages.
 *
 * The only user interaction it performs is the "no box open, add one?" prompt,
 * which is a question, not a failure.
 *
 * @param {string} barcode the text of the barcode
 * @param {Sample} sample the sample the barcode is being scanned against
 * @returns {Promise<SaveVerdict>} ok, or the reason it could not be saved
 */
export async function validateCanSave(barcode: string, sample: Sample): Promise<SaveVerdict> {
  const session = getCurrentSession();

  if (!session) {
    return scanFailure('NO_SESSION', `No session found while scanning ${barcode}`, barcode, {
      sampleId: sample.sample_id,
    });
  }

  const mission = getCurrentMission();

  // Check 1: Does this bag meet our rules for what we can scan as a sample?
  const sampleBarcodeRegex = sample.getBarcodeRegex();
  const barcodeValid = isBarcodeValidBag(barcode, sampleBarcodeRegex);

  // if it's not a valid barcode, don't bother with any additional logic
  if (!barcodeValid) {
    // an empty restriction means the default rules were used; say so instead of
    // printing an empty "Regex:" and leaving the operator guessing
    const restriction = sampleBarcodeRegex || '(default rules)';

    return scanFailure(
      'INVALID_BARCODE',
      `The barcode ${barcode} is invalid for sample ${sample.sample_id}. Regex: ${restriction}`,
      barcode,
      { sampleId: sample.sample_id, context: { regex: sampleBarcodeRegex } },
    );
  }

  const box = SampleBox.getCurrentBox();

  // Check 2: Does this bag already exist in the box or mission or a past mission?
  const duplicate = await checkForDuplicates(barcode, box, mission);

  if (duplicate) {
    return scanFailure(
      'DUPLICATE_BARCODE',
      `Barcode ${barcode} is a duplicate of sample ${duplicate.sample_id ?? '(unknown sample)'}`,
      barcode,
      {
        sampleId: sample.sample_id,
        context: { duplicateSampleId: duplicate.sample_id },
      },
    );
  }

  // we will do duplicate check even if we don't have a mission so we can
  // always identify a misplaced bag, but if we don't have a mission we don't
  // actually need to continue with any barcode validation
  if (!mission) {
    return scanFailure('NO_MISSION', `Mission not found while scanning ${barcode}`, barcode, {
      sampleId: sample.sample_id,
    });
  }

  const spec = sample.getSamplingSpec();

  if (!spec) {
    return scanFailure('NO_SPEC', `No spec found for sample ${sample.sample_id}`, barcode, {
      sampleId: sample.sample_id,
    });
  }

  const specLabName = spec.lab_name.replace(/"/g, '');
  const specLabCode = parseInt(spec.lab_code);

  return resolveBoxForScan({ session, mission, spec, sample, barcode, box, specLabName, specLabCode });
}

function multipleOpenBoxesFailure(labName: string, barcode: string, sample: Sample): ScanFailure {
  return scanFailure('MULTIPLE_OPEN_BOXES', `Multiple open boxes for ${labName} found!`, barcode, {
    sampleId: sample.sample_id,
  });
}

/**
 * Works out which box the scan may be written into: the current one, a matching
 * open box for the lab, or a freshly created one.
 *
 * Returns the box inside the ok verdict so "the scan never lands in a
 * wrong-lab box" is a property of the verdict instead of a side effect of which
 * box happens to be active.
 */
async function resolveBoxForScan(params: {
  session: Session;
  mission: Mission;
  spec: SamplingSpec;
  sample: Sample;
  barcode: string;
  box?: SampleBox;
  specLabName: string;
  specLabCode: number;
}): Promise<SaveVerdict> {
  const { session, mission, spec, sample, barcode, specLabName, specLabCode } = params;
  let box = params.box;

  if (box?.labName.replace(/"/g, '') !== specLabName) {
    const lookup = findOrActivateMatchingBox(session, specLabCode, specLabName);

    if (lookup.multipleOpenBoxesFor) {
      return multipleOpenBoxesFailure(lookup.multipleOpenBoxesFor, barcode, sample);
    }

    box = lookup.box;
  }

  if (box) {
    return { ok: true, box };
  }

  // Fail-safe: If we don't have a box, we can prompt to add it
  const boxBeforePrompt = SampleBox.getCurrentBox();
  errorTone();

  const confirm = await alertConfirm(
    `No box is currently open for ${specLabName}, would you like to add one?`,
    'no-box-open',
  );

  // A box may have been created while the confirm dialog was open
  // (e.g. user clicked "Add Box" toolbar button, which also dismisses the dialog).
  // Re-check for a matching box — must verify lab match, not just any active box.
  const postConfirmLookup = findOrActivateMatchingBox(session, specLabCode, specLabName);

  if (postConfirmLookup.multipleOpenBoxesFor) {
    return multipleOpenBoxesFailure(postConfirmLookup.multipleOpenBoxesFor, barcode, sample);
  }

  box = postConfirmLookup.box;

  if (box) {
    return { ok: true, box };
  }

  const boxAfterPrompt = SampleBox.getCurrentBox();

  // The lookup above matches by the decoded lab code, which is lost for codes
  // above 511 and for missing ones. Accept a box that is in fact for the lab this
  // sample needs, or the operator gets told "a Foo box was added, but this sample
  // needs a Foo box" for the box they just added.
  if (boxAfterPrompt && boxAfterPrompt.labName.replace(/"/g, '') === specLabName) {
    return { ok: true, box: boxAfterPrompt };
  }

  if (!confirm) {
    const boxAddedWhilePrompting =
      Boolean(boxAfterPrompt) && boxAfterPrompt?.instance_id !== boxBeforePrompt?.instance_id;

    if (!boxAddedWhilePrompting) {
      // a deliberate "no": logged for support, but not an error to shout about
      return scanFailure(
        'BOX_DECLINED',
        `No box is currently open for ${specLabName} and the operator declined to add one`,
        barcode,
        {
          sampleId: sample.sample_id,
          context: { specLabName, specLabCode, confirmed: false },
        },
      );
    }

    // The prompt was dismissed by the "Add Box" toolbar button, which creates a
    // box for the job's primary lab. That is not a decline: the operator believes
    // the box was added, so keep the scan loud instead of dropping it silently.
    return scanFailure(
      'WRONG_LAB_BOX',
      `A ${boxAfterPrompt?.labName || 'different'} box was added, but this sample needs a ${specLabName} box. Please add a ${specLabName} box, then scan again.`,
      barcode,
      {
        sampleId: sample.sample_id,
        context: { specLabName, specLabCode, activeBoxLab: boxAfterPrompt?.labName, confirmed: false },
      },
    );
  }

  // trust the created box: it was made for this spec. Never fall back to
  // SampleBox.getCurrentBox() — on failure that is the box that caused the lab
  // mismatch — and never re-match by lab code, which does not round-trip
  // through the box uid for codes above 511 or missing ones.
  const newBox = await session.addAndSelectBox(mission, spec);

  if (!newBox) {
    // addAndSelectBox already told the operator why ("No User/Mission Found")
    return scanFailure(
      'BOX_ADD_FAILED',
      `Could not create a box for ${specLabName}. Please add a box, then scan again.`,
      barcode,
      {
        sampleId: sample.sample_id,
        context: { specLabName, specLabCode, confirmed: true },
      },
    );
  }

  return { ok: true, box: newBox };
}

/**
 * Checks for duplicates in the box and mission.
 * Alerts with the richer "where is this bag" UI when it can; the caller adds
 * the reason on top of it, which is fine — a couple of messages beats silence.
 * @param box  The box to check
 * @param barcode  The barcode to check
 * @param mission  The mission to check
 * @returns  The sample that is duplicated, if any
 */
async function checkForDuplicates(barcode: string, box?: SampleBox, mission?: Mission) {
  logger.log('CHECK_FOR_DUPLICATES', { barcode, box: box?.uid, mission: mission?.name });
  let duplicatedInBox: Sample | undefined = undefined;
  let duplicatedInMission: Sample | undefined = undefined;

  // If we have a box...
  if (box) {
    duplicatedInBox = checkBoxForDuplicates(barcode, box, mission);
  }

  // Check all mission samples for duplicate barcodes.
  // Skipped/deleted samples are included on purpose: the app clears the bag
  // when a sample is skipped (or refuses to skip a bagged sample), so a
  // barcode on a skipped sample means imported/legacy data. Rejecting it keeps
  // the operator from silently creating a second owner.
  if (mission) {
    const allSamples = mission.getAllSamples();

    for (const sample of allSamples) {
      if (barcode === sample.bag_id) {
        duplicatedInMission = sample;
        break;
      }
    }
  }

  let duplicatedInPastMission: Sample | undefined = undefined;

  // if it wasn't duplicated in any of the missions in this box, then we will search all samples to be safe
  if (!duplicatedInBox && !duplicatedInMission) {
    duplicatedInPastMission = Sample.findOne((sample) => !!sample && sample.bag_id === barcode);
    const duplicatedSampleMissionInstanceId = duplicatedInPastMission?.getSampleSite()?.getMission()?.instance_id;
    const currentMissionInstanceId = mission?.instance_id;
    const sampleIdsUndefined = !duplicatedSampleMissionInstanceId || !currentMissionInstanceId;

    if (
      duplicatedInPastMission?.mission_name === mission?.name ||
      (!sampleIdsUndefined && currentMissionInstanceId === duplicatedSampleMissionInstanceId)
    ) {
      alertWarn(`Contact Stephen, he might have figured it out!`);
      logger.log('CHECK_FOR_DUPLICATES', duplicatedInPastMission);
    }
  }

  // if we have a duplicate mission barcode or a duplicate box barcode, alert the user
  if (duplicatedInBox) {
    warningTone();
    alertError(`Duplicate barcode detected in box ${box?.uid || 'UNKNOWN'}! Barcode ${barcode}`);
  } else if (duplicatedInMission || duplicatedInPastMission) {
    // we can force this null check because our boolean means one of these has to be defined, and if the first one is
    // undefined, then the second one must be defined since we're in this block
    const bag_id = duplicatedInMission?.bag_id;
    const box_uid = duplicatedInMission?.box_uid;
    const mission_name = duplicatedInMission ? duplicatedInMission.mission_name : duplicatedInPastMission!.mission_name;
    const boxQuery = SampleBox.query((box: SampleBox) => box.uid === box_uid);

    if (boxQuery.length) {
      warningTone();
      const box = boxQuery[0];
      const boxSamples = box.getSamples();
      const friendlyBoxId = box.friendlyId;
      const sampleIndex = boxSamples.findIndex((sample) => sample.bag_id === bag_id) + 1;
      await alertFullScreen(
        'We found this sample in another box',
        [
          `Bag <strong>${bag_id}</strong> is already in`,
          `Box <strong>${box_uid}</strong>, position ${sampleIndex}` + (friendlyBoxId ? ` (${friendlyBoxId})` : ''),
          `Mission <strong>${mission_name}</strong>`,
        ].join('\n'),
        ['Ok'],
      );
    }
  }

  const duplicateBarcode = duplicatedInBox || duplicatedInMission || duplicatedInPastMission;

  return duplicateBarcode;
}

function checkBoxForDuplicates(barcode: string, box: SampleBox, mission?: Mission) {
  let duplicatedInBox: Sample | undefined = undefined;
  const duplicateBoxSamples = box
    .getSamples()
    .filter((sample) => sample.bag_id === barcode && !sample.skipped_or_deleted);

  // Check the box for duplicate barcodes
  if (duplicateBoxSamples.length) {
    duplicatedInBox = duplicateBoxSamples[0];
  }

  // check the box for duplicate missions in this box
  if (mission) {
    const boxMissions = box.getSampleBoxMissions();

    if (
      boxMissions.find(
        (existMission) => existMission.job_id === mission.job_id && existMission.instance_id !== mission.instance_id,
      )
    ) {
      alertError('The mission being scanned already exists in the box! Make sure to clear the duplicate!');
    }
  }

  return duplicatedInBox;
}

/**
 * Updates all data class instance's with new barcode and SampleBox id
 * @param {number} modifiedSampleInstanceId The sample instance id being modified
 * @param {string} barcode The new barcode to replace existing barcode
 * @param {boolean} override An override switch that ignores box check
 * @param {number} [sampleBoxId] The box the caller resolved (the scan verdict).
 *   Awaited dialogs between the verdict and the write can make another box
 *   active, so the scan passes its box instead of re-reading the active one.
 */
export async function setSampleBarcodes(
  modifiedSampleInstanceId: number,
  barcode: string,
  override: boolean,
  sampleBoxId?: number,
): Promise<SampleErrorMap> {
  logger.log('SET_SAMPLE_BARCODES', { modifiedSampleInstanceId, barcode, override, sampleBoxId });

  const modifiedSample = Sample.get(modifiedSampleInstanceId);

  if (!modifiedSample || modifiedSample.sample_id === undefined) {
    logger.log('Failed to set sample barcodes');

    return {} as SampleErrorMap;
  }

  const sampleBox = sampleBoxId === undefined ? SampleBox.getCurrentBox() : SampleBox.get(sampleBoxId);
  const mission = getCurrentMission();

  // an explicitly resolved box can be closed while the scan's dialogs are open
  if (!sampleBox || sampleBox.closed) {
    throw new Error('Cannot retrieve box');
  }

  if (!override && modifiedSample.SampleBox_id !== undefined && sampleBox.instance_id !== modifiedSample.SampleBox_id) {
    logger.log(
      'SET_SAMPLE_BARCODES',
      `Can not modify sample outside of current box! ${override} ${modifiedSample.SampleBox_id}, ${sampleBox.instance_id}`,
    );

    throw new Error('Can not modify sample outside of current box!');
  } else {
    if (!mission) {
      logger.log('Cannot retrieve mission');

      throw new Error('Cannot retrieve mission');
    }

    await modifiedSample.setBarcode(barcode, sampleBox?.instance_id);
    cancelConfirmTag('empty-box-close');
    const msg = { data: `${modifiedSample.sample_id};${barcode}` };

    dispatchRosMsgPub({
      hostname: getCurrentSession()?.robot_hostname || '',
      topic: OUTBOUND_TOPICS.APP_BARCODE_COMPLETED,
      compress: false,
      tag: 'barcode',
      msg,
    });

    const allSamples = mission.getAllSamples();
    const boxSamples = sampleBox?.getSamples();
    const sampleErrMap = generateErrorMap(allSamples);
    const boxErrMap = generateErrorMap(boxSamples);

    return { ...sampleErrMap, ...boxErrMap };
  }
}
